import { Card, Group, Loader, Modal, ModalProps, ScrollArea, Stack, Text } from '@mantine/core';
import { IconLicense } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMetricsMessage, MetricsMessageItem, useBot, BotQuota } from '@mygpt/app';
import QuotaItem from '@/components/Settings/MessageUsageModal/QuotaItem';

interface Props extends ModalProps {
  robotPlanInfo?: BotQuota;
  time?: (Date | string)[];
}

export default ({ robotPlanInfo, time, ...rest }: Props) => {
  const { t } = useTranslation();
  const { botId } = useBot();
  const { opened } = rest;

  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [data, setData] = useState<MetricsMessageItem[]>([]);

  const totalUsage = useMemo(() => {
    return data.reduce((accumulator, currentValue) => accumulator + currentValue.count, 0);
  }, [data]);

  const initData = () => {
    getMetricsMessage({
      robot_id: botId,
      page: 1,
      size: 1000,
      ...(time &&
        !time.some((item) => !item) && {
          start_date: dayjs(time[0]).startOf('day').toISOString(),
          end_date: dayjs(time[1]).endOf('day').toISOString(),
        }),
    })
      .then((res) => {
        const { items } = res.data;
        setData(items);
      })
      .finally(() => {
        setDataLoading(false);
      });
  };

  useEffect(() => {
    opened && initData();
  }, [opened]);

  return (
    <Modal {...rest} size={'xl'}>
      <Text size={'xs'} c="gray.5">
        {t('settings.quota.modal.desc')}
      </Text>
      <Card
        shadow="sm"
        p="lg"
        radius="md"
        className="group transform transition-transform duration-300 hover:shadow-md"
      >
        <Stack>
          <Group>
            <IconLicense />
            <Text>{t('settings.quota.modal.gptbase_message_quota')}</Text>
          </Group>
          <section>
            <Group justify="space-between" mb={10}>
              {time && !time.some((item) => !item) && (
                <Text size={'xs'} c="gray">
                  {dayjs(time[0]).format('YYYY-MM-DD')} ~ {dayjs(time[1]).format('YYYY-MM-DD')}
                </Text>
              )}

              <Text size={'xs'} c="gray">
                {t('settings.quota.modal.message', {
                  usage: totalUsage,
                })}
              </Text>
            </Group>
          </section>
        </Stack>
      </Card>
      <Text mt={30}>{t('settings.quota.modal.consumed_logs')}</Text>

      {dataLoading && !data.length && (
        <div className="mt-6 flex items-center justify-center h-[300px]">
          <Loader />
        </div>
      )}

      {!dataLoading && (
        <ul className="mt-6 divide-y divide-slate-200 min-h-[300px]">
          <ScrollArea.Autosize mah={'40vh'} mx="auto">
            {data.map((item, index) => {
              return (
                <li key={index} className="py-2">
                  <QuotaItem item={item} />
                </li>
              );
            })}
          </ScrollArea.Autosize>
        </ul>
      )}

      <Group justify="flex-end" mt={20} mr={10}>
        <Text>{t('settings.quota.modal.cumulative_usage')}:</Text>
        <Text c="violet">{totalUsage}</Text>
      </Group>
    </Modal>
  );
};
